import type { CustomerEmitEventInput } from '../../shared/analytics/customerAnalyticsClient.js';

/**
 * Analytics metadata helpers for the MOBILE shop — keys must match
 * `analyticsEventCatalog.ts` (`.strict()` schemas). Kiosk linkage belongs on
 * the analytics session (`startSession.kioskId`), not in event metadata.
 */

/** G1 — organic shop entry (`kioskId === null` still emits). */
export function emitShopScreenViewed(
  track: (input: CustomerEmitEventInput) => void,
  kioskId: number | null
): void {
  track({
    eventName: 'screen_viewed',
    flow: 'RETAIL',
    screenName: 'shop',
    metadata:
      kioskId === null
        ? { screen: 'shop_organic' }
        : { screen: 'shop' },
  });
}

export function shopCatalogLoadedMetadata(): Record<string, unknown> {
  return { interaction_type: 'catalog_loaded' };
}

export function shopProductAddedMetadata(
  productId: number,
  productName: string,
  viaBarcode?: boolean
): Record<string, unknown> {
  return {
    productId,
    productName,
    ...(viaBarcode === true ? { interaction_type: 'barcode_scan' } : {}),
  };
}

export function shopCartViewedMetadata(
  lineCount: number,
  totalAmountMinor: number
): Record<string, unknown> {
  return {
    cart_item_count: lineCount,
    cart_total_cents: totalAmountMinor,
  };
}

export function shopCheckoutStartedMetadata(
  lineCount: number,
  totalAmountMinor: number
): Record<string, unknown> {
  return {
    itemCount: lineCount,
    amountMinor: totalAmountMinor,
  };
}

export function emitPaymentSubmitted(
  track: (input: CustomerEmitEventInput) => void,
  screenName: string,
  paymentMethod: 'QR' | 'CARD' | 'CASH',
  flow: 'RETAIL' | 'DONATION' = 'RETAIL',
): void {
  track({
    eventName: 'payment_submitted',
    flow,
    screenName,
    metadata: { payment_method: paymentMethod },
  });
}

export function emitRetailOrderAbandoned(
  track: (input: CustomerEmitEventInput) => void,
  screenName: string,
  cartItemCount: number,
  cartTotalCents?: number,
): void {
  track({
    eventName: 'retail_order_abandoned',
    flow: 'RETAIL',
    screenName,
    metadata: {
      cart_item_count: cartItemCount,
      ...(cartTotalCents !== undefined ? { cart_total_cents: cartTotalCents } : {}),
    },
  });
}
